import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui'
import type { CareAdvocateCallbackSlot, CareAdvocateCallbackSlotPickerData } from '@/types'
import { cn } from '@/utils/cn'

export interface SlotDayAccordionProps {
  days: CareAdvocateCallbackSlotPickerData['days']
  selectedSlotUtc: string | null
  onSelect: (dayLabel: string, slot: CareAdvocateCallbackSlot) => void
}

/**
 * One collapsible section per day, each holding that day's tappable time pills.
 * The first day starts expanded so a time is one tap away; the remaining days
 * start collapsed to keep the widget short in the chat transcript. One day is
 * open at a time — expanding another collapses the current one.
 */
export function SlotDayAccordion({
  days,
  selectedSlotUtc,
  onSelect,
}: SlotDayAccordionProps) {
  const hasSelection = selectedSlotUtc !== null

  return (
    <Accordion
      type="single"
      collapsible
      defaultValue={days[0]?.dayLabel}
      className="space-y-2"
    >
      {days.map((day) => (
        <AccordionItem key={day.dayLabel} value={day.dayLabel} className="border-b-0">
          <AccordionTrigger className="rounded-md border border-gray-200 bg-gray-100 px-3 text-sm text-gray-900 hover:bg-gray-200">
            <span>{day.dayLabel}</span>{' '}
            <span className="ml-auto text-xs font-normal text-gray-500">
              {day.slots.length} {day.slots.length === 1 ? 'slot' : 'slots'}
            </span>
          </AccordionTrigger>
          <AccordionContent className="px-3 pt-2">
            <div
              className="flex flex-wrap gap-2"
              role="group"
              aria-label={`Available times on ${day.dayLabel}`}
            >
              {day.slots.map((slot) => {
                const isSelected = slot.slotStartUtc === selectedSlotUtc
                return (
                  <button
                    key={slot.slotStartUtc}
                    type="button"
                    disabled={hasSelection && !isSelected}
                    aria-pressed={isSelected}
                    onClick={() => onSelect(day.dayLabel, slot)}
                    className={cn(
                      'rounded-full border px-3 py-1.5 text-sm cursor-pointer',
                      'transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1',
                      'disabled:cursor-not-allowed disabled:opacity-50',
                      isSelected
                        ? 'border-primary-600 bg-primary-600 text-white'
                        : 'border-primary-200 bg-white text-primary-700 hover:bg-primary-50',
                    )}
                  >
                    {slot.localLabel}
                  </button>
                )
              })}
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  )
}
